/**
 * Portal home — matches the "Your business. Your data. Your answers." spec.
 *
 * Layout beats:
 *   1. Greeting + "All systems on track" freshness pill
 *   2. Four hero stat cards (Safe to spend / Tax reserve / GST position / Bank balance)
 *   3. GST due reminder banner
 *   4. "What would you like to do?" — three action cards (Can I claim / Can I afford / Ask accountant)
 *   5. Trust row (secure · built-for-you · actionable)
 *
 * All numbers are deterministic reads from /api/portal/snapshot. The action
 * cards route to their dedicated pages (which we'll flesh out in follow-up
 * sprints — routes exist but pages are stubs for now).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ChevronRight, CheckCircle2, Calendar, ShieldCheck, Target, User,
    FileCheck2, LineChart, MessageCircle, Lock, Sparkles, Users,
    Wallet, UserPlus, TrendingUp, Rocket,
} from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MorningBriefHero from "@/components/MorningBriefHero";
import JobStatusOdometer from "@/components/JobStatusOdometer";
import SpendInsightsTile from "@/components/SpendInsightsTile";
import ConnectDataSourceCard from "@/components/ConnectDataSourceCard";
import MonthlySalesCard from "@/components/MonthlySalesCard";
import PortalTaxProvisioningCard from "@/components/portal/PortalTaxProvisioningCard";
import ActivationChecklist from "@/components/portal/ActivationChecklist";
import { portalGreetingName } from "@/lib/greeting";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
}

export default function PortalDashboard() {
    const { user, activeClient, activeClientId, combinedView, clients } = useAuth();
    const [snap, setSnap] = useState(null);
    const [features, setFeatures] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const snapshotReq = combinedView
            ? api.get("/portal/snapshot/combined").catch(() => ({ data: null }))
            : api.get("/portal/snapshot", { params: activeClientId ? { client_id: activeClientId } : {} }).catch(() => ({ data: null }));
        Promise.all([
            snapshotReq,
            api.get("/portal/features").catch(() => ({ data: null })),
        ]).then(([s, f]) => {
            setSnap(s.data);
            setFeatures(f.data?.resolved || null);
            setLoaded(true);
        });
    }, [activeClientId, combinedView]);

    const showMorningBrief = features?.morning_brief === true;
    const showOdometer = features?.job_status_odometer === true;
    const showSpend = features?.spend_insights === true;
    // Home-screen sections default TRUE if the flag is missing (backwards-
    // compat with older tenants). Explicit `false` hides them.
    const showHero = features?.hero_snapshot !== false;
    const showClaim = features?.action_claim !== false;
    const showAfford = features?.action_afford !== false;
    const showAsk = features?.action_ask !== false;
    const showDebtors = features?.action_debtors !== false;
    const showPaySelf = features?.action_pay_self !== false;
    const showHire = features?.action_hire !== false;
    const showPriceChange = features?.action_price_change !== false;
    const showGrowthPlan = features?.action_growth_plan !== false;
    const showWealthPlan = features?.action_wealth_plan !== false;
    const showBreakEven = features?.action_break_even !== false;
    const showTrust = features?.trust_strip !== false;
    const anyAction = showClaim || showAfford || showAsk || showDebtors || showPaySelf || showHire || showPriceChange || showGrowthPlan || showWealthPlan;

    return (
        <div className="space-y-6 pb-8" data-testid="portal-dashboard">
            <ActivationChecklist />
            {/* Greeting */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-[#5E636E] mb-1" data-testid="portal-trading-name">
                        NZ Taxation Limited t/a ClientOps
                    </div>
                    <h1 className="font-display text-2xl sm:text-3xl tracking-tight font-semibold" data-testid="portal-greeting">
                        {greeting()}, {portalGreetingName(user, activeClient)}! <span aria-hidden>👋</span>
                    </h1>
                    <p className="mt-1 text-sm text-[#5E636E]">
                        {combinedView
                            ? `Viewing combined totals across all ${(clients || []).length} of your entities.`
                            : (anyAction ? "What would you like to do today?" : "Welcome back.")}
                    </p>
                </div>
                {showHero && <StatusPill snap={snap} loaded={loaded} />}
            </div>

            {/* Owner-mode self-connect prompt — rendered nowhere except tenant.mode==='owner'. */}
            <ConnectDataSourceCard />

            {/* Monthly sales — the "even a 15yo can read it" report. Shows
                Jan → current month bars with a compact caption. Empty state
                nudges to the Connect card. Rendered for every portal user. */}
            <MonthlySalesCard />
            <PortalTaxProvisioningCard />

            {/* --- What would you like to do? (primary home content) ------- */}
            {anyAction && (
            <section>
                <div className="mb-3">
                    <h2 className="font-display text-lg font-semibold">What would you like to do?</h2>
                    <p className="text-xs text-[#5E636E] mt-0.5">All answers are based on your actual data.</p>
                </div>
                <div className="grid gap-3">
                    {showBreakEven && (
                    <ActionCard
                        title="SALES TARGET & BREAK-EVEN"
                        body="How much sales do I need next month, next quarter or this FY to cover costs? Plain-English answer + daily and weekly targets."
                        icon={Target}
                        gradient="from-teal-500 to-emerald-600"
                        badge="This period"
                        to="/portal/break-even"
                        testId="action-break-even"
                    />
                    )}
                    {showClaim && (
                    <ActionCard
                        title="CAN I CLAIM IT?"
                        body="Check if something is tax deductible based on your business and get your estimated tax saving."
                        icon={FileCheck2}
                        gradient="from-emerald-500 to-emerald-700"
                        badge="Uses your data"
                        to="/portal/claim"
                        testId="action-claim"
                    />
                    )}
                    {showAfford && (
                    <ActionCard
                        title="CAN I AFFORD IT?"
                        body="See the financial impact of a purchase before you decide. Know if it fits your cash flow and goals."
                        icon={LineChart}
                        gradient="from-sky-500 to-blue-700"
                        badge="Personalised for you"
                        to="/portal/afford"
                        testId="action-afford"
                    />
                    )}
                    {showAsk && (
                    <ActionCard
                        title="ASK YOUR ACCOUNTANT"
                        body="Get an instant answer from AI using your data. Escalate to your accountant when you need expert advice."
                        icon={MessageCircle}
                        gradient="from-violet-500 to-purple-700"
                        badge="AI + Human backup"
                        to="/portal/ask"
                        testId="action-ask"
                    />
                    )}
                    {showDebtors && (
                    <ActionCard
                        title="WHO OWES YOU MONEY?"
                        body={activeClient?.entity_type === "sole_trader"
                            ? "See every unpaid invoice from Prosaic grouped by how overdue they are — and chase them in one tap."
                            : "See every unpaid invoice from Xero grouped by how overdue they are — and chase them in one tap."}
                        icon={Users}
                        gradient="from-rose-500 to-red-700"
                        badge={activeClient?.entity_type === "sole_trader" ? "Live from Prosaic" : "Live from Xero"}
                        to="/portal/debtors"
                        testId="action-debtors"
                    />
                    )}
                    {showPaySelf && (
                    <ActionCard
                        title="HOW MUCH CAN I PAY MYSELF?"
                        body="Get a sustainable owner draw — after expenses, tax reserve and a healthy buffer — based on your real numbers."
                        icon={Wallet}
                        gradient="from-amber-500 to-orange-700"
                        badge="Uses your data"
                        to="/portal/pay-self"
                        testId="action-pay-self"
                    />
                    )}
                    {showHire && (
                    <ActionCard
                        title="CAN I AFFORD TO HIRE?"
                        body="Model a new staff salary against your current profit and cash flow. See when they pay for themselves."
                        icon={UserPlus}
                        gradient="from-teal-500 to-emerald-800"
                        badge="What-if"
                        to="/portal/hire"
                        testId="action-hire"
                    />
                    )}
                    {showPriceChange && (
                    <ActionCard
                        title="WHAT IF I RAISED MY PRICES?"
                        body="See the revenue and margin lift a small price rise unlocks — before you tell a single customer."
                        icon={TrendingUp}
                        gradient="from-fuchsia-500 to-pink-700"
                        badge="What-if"
                        to="/portal/price-change"
                        testId="action-price-change"
                    />
                    )}
                    {showGrowthPlan && (
                    <ActionCard
                        title="GROWTH PLAN"
                        body="Set next year's revenue target and see the monthly milestones you need to hit to get there."
                        icon={Rocket}
                        gradient="from-indigo-500 to-blue-800"
                        badge="Plan ahead"
                        to="/portal/growth-plan"
                        testId="action-growth-plan"
                    />
                    )}
                    {showWealthPlan && (
                    <ActionCard
                        title="GROW MY WEALTH"
                        body="Project your net worth years ahead with a property or share pot. NZ tax, inflation and opportunity cost all baked in."
                        icon={Rocket}
                        gradient="from-slate-700 to-black"
                        badge="Long-term"
                        to="/portal/wealth-plan"
                        testId="action-wealth-plan"
                    />
                    )}
                </div>
            </section>
            )}

            {/* Four hero cards (financial snapshot — below actions) */}
            {showHero && (
            <section>
                <div className="mb-3 flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-display text-lg font-semibold">
                            {combinedView ? "Combined snapshot" : "Your financial snapshot"}
                        </h2>
                        <p className="text-xs text-[#5E636E] mt-0.5">
                            {combinedView
                                ? `Live totals across all ${snap?.entity_count || (clients || []).length} entities you own.`
                                : "Live figures from your accounting data."}
                        </p>
                    </div>
                    {combinedView && snap?.entity_names?.length && (
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] text-emerald-900 font-medium" data-testid="combined-scope-chip">
                            <Users className="h-3 w-3" /> {snap.entity_count} entities · combined
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="portal-hero-cards">
                    <HeroCard
                        testId="card-safe-to-spend"
                        label="Safe to spend"
                        value={snap?.cards?.safe_to_spend?.value}
                        caption={snap?.cards?.safe_to_spend?.label || "This month"}
                        flag={snap?.cards?.safe_to_spend?.flag}
                        tone="value-emerald"
                        loaded={loaded}
                    />
                    <HeroCard
                        testId="card-tax-reserve"
                        label="Tax reserve"
                        value={snap?.cards?.tax_reserve?.value}
                        caption={snap?.cards?.tax_reserve?.label}
                        flag={snap?.cards?.tax_reserve?.flag}
                        tone="value-emerald"
                        loaded={loaded}
                    />
                    <HeroCard
                        testId="card-gst-position"
                        label="GST position"
                        value={snap?.cards?.gst_position?.value}
                        caption={snap?.cards?.gst_position?.label}
                        flag={snap?.cards?.gst_position?.flag}
                        tone="value-emerald"
                        loaded={loaded}
                    />
                    <HeroCard
                        testId="card-bank-balance"
                        label="Bank balance"
                        value={snap?.cards?.bank_balance?.value}
                        caption={snap?.cards?.bank_balance?.label}
                        flag={snap?.cards?.bank_balance?.flag}
                        tone="value-dark"
                        loaded={loaded}
                    />
                </div>
                {combinedView && snap?.per_entity?.length && (
                    <div className="mt-4 rounded-lg border border-[#E4E4E7] bg-white overflow-hidden" data-testid="combined-per-entity-breakdown">
                        <div className="px-3 py-2 border-b border-[#E4E4E7] bg-[#F8F8F5] flex items-center justify-between">
                            <div className="co-label">Breakdown by entity</div>
                            <div className="text-[11px] text-[#5E636E]">Tap a row to focus on that one</div>
                        </div>
                        <div className="divide-y divide-[#F1F1F3]">
                            {snap.per_entity.map((e) => (
                                <PerEntityRow key={e.client_id} entity={e} />
                            ))}
                        </div>
                    </div>
                )}
            </section>
            )}

            {/* GST due banner */}
            {showHero && snap?.gst_reminder && <GstReminder r={snap.gst_reminder} />}

            {/* Trust strip */}
            {showTrust && (
            <div className="grid grid-cols-3 gap-2 pt-2" data-testid="trust-strip">
                <TrustItem icon={ShieldCheck} title="Your data is secure" hint="Bank-level security" />
                <TrustItem icon={User} title="Built for your business" hint="Your data, your context" />
                <TrustItem icon={Target} title="Actionable & on track" hint="Helping you every day" />
            </div>
            )}

            {/* --- Legacy tiles preserved (feature-flagged) ------------------ */}
            {showMorningBrief && (
                <section className="pt-2"><MorningBriefHero /></section>
            )}
            {showOdometer && (
                <section><JobStatusOdometer /></section>
            )}
            {showSpend && (
                <section><SpendInsightsTile /></section>
            )}
        </div>
    );
}

/* ---------------- Status pill ---------------- */
function StatusPill({ snap, loaded }) {
    if (!loaded) {
        return (
            <div className="rounded-xl bg-white border border-[#E4E4E7] px-3.5 py-2.5 flex items-center gap-2 text-xs text-[#5E636E] shrink-0">
                <div className="h-2 w-2 rounded-full bg-zinc-300 animate-pulse" /> Loading…
            </div>
        );
    }
    const ok = snap?.system_status === "on_track";
    return (
        <div
            className={"rounded-xl border px-3.5 py-2.5 flex items-center gap-2.5 shrink-0 " + (
                ok
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-amber-50 border-amber-200"
            )}
            data-testid="portal-status-pill"
        >
            <span className={"grid h-6 w-6 place-items-center rounded-full " + (ok ? "bg-emerald-600" : "bg-amber-500")}>
                <CheckCircle2 className="h-3.5 w-3.5 text-white" />
            </span>
            <div className="leading-tight">
                <div className={"text-xs font-semibold " + (ok ? "text-emerald-900" : "text-amber-900")}>
                    {snap?.status_text || "Status"}
                </div>
                <div className={"text-[10px] " + (ok ? "text-emerald-700" : "text-amber-700")}>
                    {snap?.updated_hint || ""}
                </div>
            </div>
        </div>
    );
}

/* ---------------- Hero stat card ---------------- */
function HeroCard({ testId, label, value, caption, flag, tone, loaded }) {
    const isDark = tone === "value-dark";
    const valueColour =
        !loaded ? "text-[#B0B4BB]"
            : value == null ? "text-[#5E636E]"
                : isDark ? "text-[#0F1115]"
                    : flag === "bad" ? "text-rose-600"
                        : flag === "warn" ? "text-amber-600"
                            : flag === "info" ? "text-sky-700"
                                : "text-emerald-700";
    return (
        <div
            className="rounded-xl bg-white border border-[#E4E4E7] p-3 flex flex-col justify-between min-h-[110px]"
            data-testid={testId}
        >
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5E636E]">{label}</div>
            <div>
                <div className={"font-display text-xl sm:text-2xl tabular-nums font-semibold leading-none mt-2 " + valueColour}>
                    {loaded && value != null ? NZD.format(value) : loaded ? "—" : ""}
                </div>
                <div className="mt-2 text-[11px] text-[#5E636E]">{loaded ? (caption || "") : "Loading…"}</div>
                {/* Tiny progress track — visual echo of the reference image */}
                <div className="mt-1.5 h-[3px] rounded-full bg-[#F1F2F4] overflow-hidden">
                    <div className={
                        "h-full transition-all duration-700 " + (
                            flag === "bad" ? "bg-rose-500 w-1/4"
                                : flag === "warn" ? "bg-amber-500 w-1/2"
                                    : flag === "ok" ? "bg-emerald-600 w-full"
                                        : flag === "info" ? "bg-sky-400 w-3/4"
                                            : "bg-zinc-200 w-1/6"
                        )
                    } />
                </div>
            </div>
        </div>
    );
}

/* ---------------- GST due banner ---------------- */
function GstReminder({ r }) {
    const days = r.days_until;
    const overdue = days != null && days < 0;
    const soon = days != null && days >= 0 && days <= 14;
    const tone = overdue ? "bg-rose-50 border-rose-200 text-rose-900"
        : soon ? "bg-sky-50 border-sky-200 text-sky-900"
            : "bg-emerald-50 border-emerald-200 text-emerald-900";
    const iconTone = overdue ? "bg-rose-500" : soon ? "bg-sky-500" : "bg-emerald-600";
    const line = overdue
        ? `GST was due ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`
        : days === 0 ? "GST is due today"
            : days === 1 ? "GST is due tomorrow"
                : `GST is due in ${days} days`;

    return (
        <Link
            to="/portal/tax-liability"
            className={"flex items-center justify-between gap-3 rounded-xl border p-3.5 transition-colors hover:opacity-95 " + tone}
            data-testid="gst-reminder-banner"
        >
            <div className="flex items-center gap-3 min-w-0">
                <div className={"grid h-9 w-9 place-items-center rounded-lg text-white shrink-0 " + iconTone}>
                    <Calendar className="h-4.5 w-4.5" />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{line}</div>
                    <div className="text-[11px] opacity-80 truncate">Due date: {r.due_date_display}</div>
                </div>
            </div>
            <div className="inline-flex items-center gap-1 text-xs font-medium shrink-0">
                View calendar <ChevronRight className="h-3.5 w-3.5" />
            </div>
        </Link>
    );
}

/* ---------------- Big gradient action card ---------------- */
function ActionCard({ title, body, icon: Icon, gradient, badge, to, testId }) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className={"group relative rounded-2xl bg-gradient-to-br " + gradient + " text-white p-5 pr-16 shadow-sm hover:shadow-md transition-shadow overflow-hidden"}
        >
            <div className="flex items-start gap-4">
                <div className="grid h-14 w-14 place-items-center rounded-full bg-white/95 text-[#0F1115] shrink-0 shadow-sm">
                    <Icon className="h-6 w-6" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                    <div className="text-[15px] font-semibold tracking-tight leading-snug">
                        {title}
                    </div>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-white/90">{body}</p>
                </div>
            </div>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full bg-white/15 group-hover:bg-white/25 transition-colors">
                <ChevronRight className="h-5 w-5 text-white" />
            </div>
            <div className="absolute bottom-2 right-3 inline-flex items-center gap-1 text-[10px] font-medium text-white/85">
                <Lock className="h-3 w-3" /> {badge}
            </div>
        </Link>
    );
}

/* ---------------- Trust strip item ---------------- */
function TrustItem({ icon: Icon, title, hint }) {
    return (
        <div className="rounded-xl bg-white border border-[#E4E4E7] p-2.5 flex items-start gap-2">
            <Icon className="h-4 w-4 text-[#5E636E] shrink-0 mt-0.5" />
            <div className="min-w-0">
                <div className="text-[11px] font-semibold text-[#0F1115] leading-tight">{title}</div>
                <div className="text-[10px] text-[#5E636E] mt-0.5 leading-tight">{hint}</div>
            </div>
        </div>
    );
}

const _combined_fmt = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });

function PerEntityRow({ entity }) {
    const { setActiveClientId } = useAuth();
    function fmt(v) {
        return v === null || v === undefined ? <span className="text-[#B0B0B0]">—</span> : _combined_fmt.format(v);
    }
    return (
        <button
            type="button"
            onClick={() => setActiveClientId(entity.client_id)}
            className="w-full text-left px-3 py-2.5 grid grid-cols-2 sm:grid-cols-5 gap-2 items-center hover:bg-zinc-50 transition-colors"
            data-testid={`combined-entity-row-${entity.client_id}`}
        >
            <div className="col-span-2 sm:col-span-2 min-w-0">
                <div className="text-sm font-medium truncate">{entity.client_name}</div>
                <div className="text-[10px] text-[#5E636E]">Tap to focus →</div>
            </div>
            <div className="text-right sm:text-left">
                <div className="text-[10px] uppercase tracking-wider text-[#5E636E]">Safe to spend</div>
                <div className="text-sm font-semibold text-emerald-700">{fmt(entity.safe_to_spend)}</div>
            </div>
            <div className="text-right sm:text-left">
                <div className="text-[10px] uppercase tracking-wider text-[#5E636E]">Tax reserve</div>
                <div className="text-sm font-semibold">{fmt(entity.tax_reserve)}</div>
            </div>
            <div className="text-right sm:text-left">
                <div className="text-[10px] uppercase tracking-wider text-[#5E636E]">Bank</div>
                <div className="text-sm font-semibold">{fmt(entity.bank_balance)}</div>
            </div>
        </button>
    );
}
